/* eslint-disable no-nested-ternary */

import { api, authenticatedApi, authenticatedFormDataApi, formDataApi } from '../config';
import { IDeleteServiceProps, IGetServiceProps, IPatchServiceProps, IPostServiceProps } from './@types';

/**
 * The function `getService` is an asynchronous function that makes a GET request to a specified API
 * endpoint and returns the response data, optionally converting it into instances of a specified class
 * module.
 * @param {IGetServiceProps}  - - `authenticated`: a boolean indicating whether the API request should
 * be authenticated or not.
 * @returns either an array of instances of the provresourceIdentifiered ClassModule, or the response data itself if
 * ClassModule is not provresourceIdentifiered.
 */
export const getService = async ({
  authenticated,
  baseUrl,
  path,
  ClassModule,
  params,
  resourceIdentifier,
}: IGetServiceProps) => {
  try {
    // Construct full URL by combining baseUrl and path
    const fullPath = `${baseUrl}${path}`;
    // Remove trailing slash from path if it exists to avoid double slashes
    const normalizedPath = fullPath.endsWith('/') ? fullPath.slice(0, -1) : fullPath;
    const response = authenticated
      ? await authenticatedApi.get(`${normalizedPath}${resourceIdentifier ? `/${resourceIdentifier}` : ''}`, { params })
      : await api.get(`${normalizedPath}${resourceIdentifier ? `/${resourceIdentifier}` : ''}`, { params });
    const instances = ClassModule
      ? Array.isArray(response.data)
        ? response.data.map((item: any) => {
            return new ClassModule(item);
          })
        : new ClassModule(response.data)
      : null;
    return instances || response.data;
  } catch (err: any) {
    throw new Error(err.response.data.detail || err.response.data.Message || err.response.data.message);
  }
};

/**
 * The `patchService` function sends a PATCH request to a specified URL with optional authentication
 * and payload, and returns the response data.
 * @param {IPatchServiceProps}  - - `authenticated`: a boolean indicating whether the request should be
 * authenticated or not.
 * @returns the `data` property of the `response` object.
 */
export const patchService = async ({
  authenticated,
  baseUrl,
  path,
  payload,
  resourceIdentifier,
  formData,
}: IPatchServiceProps) => {
  try {
    // Construct full URL by combining baseUrl and path
    const fullPath = `${baseUrl}${path}`;
    // Remove trailing slash from path if it exists to avoid double slashes
    const normalizedPath = fullPath.endsWith('/') ? fullPath.slice(0, -1) : fullPath;
    
    // For endpoints that don't require a resource identifier (like /user/ for current user)
    // we allow resourceIdentifier to be undefined
    const url = resourceIdentifier !== undefined
      ? `${normalizedPath}/${resourceIdentifier}/`
      : `${normalizedPath}/`;
    
    const response = authenticated
      ? formData
        ? await authenticatedFormDataApi.patch(url, payload)
        : await authenticatedApi.patch(url, payload)
      : await api.patch(url, payload);
    return response.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.response?.data?.detail || err.message || 'An unknown error occurred');
  }
};

/**
 * The `postService` function is an asynchronous function that sends a POST request to a specified URL
 * with optional authentication and form data, and returns the response data.
 * @param {IPostServiceProps}  - - `authenticated`: a boolean value indicating whether the request
 * should be authenticated or not.
 * @returns the `data` property of the `response` object.
 */
export const postService = async ({ authenticated, baseUrl, path, payload, formData }: IPostServiceProps) => {
  try {
    // Construct full URL by combining baseUrl and path
    const fullPath = `${baseUrl}${path}`;
    // Remove trailing slash from path if it exists to avoid double slashes
    const normalizedPath = fullPath.endsWith('/') ? fullPath.slice(0, -1) : fullPath;
    const response = authenticated
      ? formData
        ? await authenticatedFormDataApi.post(`${normalizedPath}/`, payload)
        : await authenticatedApi.post(`${normalizedPath}/`, payload)
      : formData
        ? await formDataApi.post(`${normalizedPath}/`, payload)
        : await api.post(`${normalizedPath}/`, payload);
    return response.data;
  } catch (err: any) {
    throw new Error(err.response.data.message);
  }
};

/**
 * The `deleteService` function is an asynchronous function that sends a DELETE request to delete a
 * resource identified by `resourceIdentifier` at the specified `baseUrl` and `path`, with optional
 * `params`, and returns the response data.
 * @param {IDeleteServiceProps}  - - `authenticated`: a boolean value indicating whether the request
 * should be made with an authenticated API or not.
 * @returns the `response.data` from the API call.
 */
export const deleteService = async ({
  authenticated,
  baseUrl,
  path,
  resourceIdentifier,
  params,
}: IDeleteServiceProps) => {
  try {
    // Construct full URL by combining baseUrl and path
    const fullPath = `${baseUrl}${path}`;
    // Remove trailing slash from path if it exists to avoid double slashes
    const normalizedPath = fullPath.endsWith('/') ? fullPath.slice(0, -1) : fullPath;
    const response = authenticated
      ? await authenticatedApi.delete(`${normalizedPath}/${resourceIdentifier}/`, { params })
      : await api.delete(`${normalizedPath}/${resourceIdentifier}/`, { params });
    return response.data;
  } catch (err: any) {
    throw new Error(err.response.data.detail || err.response.data.Message || err.response.data.message);
  }
};

// Add a new service to fetch links with click counts
export const getLinksWithAnalytics = async (authenticated = true) => {
  try {
    const response = authenticated
      ? await authenticatedApi.get('/api/v1/link/')
      : await api.get('/api/v1/link/');
    return response.data.result || response.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch links with analytics');
  }
};
